import { Injectable, Injector, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, defer, throwError } from 'rxjs';
import { catchError, finalize, shareReplay, switchMap, tap } from 'rxjs/operators';
import { AwsService } from '../aws.service';
import { AuthService } from '../auth.service';
import { SessionExpiredDialogComponent } from '../../components/auth/session-expired-dialog/session-expired-dialog.component';

export class SessionEndedError extends Error {
  constructor() {
    super('Session ended by the user');
  }
}

@Injectable({ providedIn: 'root' })
export class SessionRefreshService {
  private readonly dialog = inject(MatDialog);
  // AwsService et AuthService déclenchent des requêtes HTTP dès leur construction : on les résout à la demande pour éviter une dépendance circulaire avec l'intercepteur.
  private readonly injector = inject(Injector);

  // Partagé entre toutes les requêtes en échec simultané : une seule popup et un seul appel à Cognito.
  private pending$: Observable<string> | null = null;

  /**
   * Demande à l'utilisateur s'il veut rester connecté.
   * Émet le nouvel idToken s'il accepte, sinon le déconnecte et émet une SessionEndedError.
   */
  askToRefresh(): Observable<string> {
    if (!this.pending$) {
      this.pending$ = defer(() =>
        this.dialog.open<SessionExpiredDialogComponent, void, boolean>(SessionExpiredDialogComponent, {
          disableClose: true,
          width: '420px'
        }).afterClosed()
      ).pipe(
        switchMap(stayConnected => stayConnected
          ? this.injector.get(AwsService).refreshIdToken().pipe(
              catchError(() => this.endSession())
            )
          : this.endSession()
        ),
        tap(idToken => this.injector.get(AuthService).setToken(idToken)),
        finalize(() => this.pending$ = null),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.pending$;
  }

  private endSession(): Observable<never> {
    this.injector.get(AuthService).logout();
    return throwError(() => new SessionEndedError());
  }
}
